import React, { useState, useEffect } from 'react';
import apiClient from '../../assets/scripts/services/apiClient';
import authService from '../../assets/scripts/auth/authService';

const FeatureRoadmapBuilder = () => {
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState('');
  const [introText, setIntroText] = useState('');
  const [features, setFeatures] = useState(['']);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');

  useEffect(() => {
    loadApplications();
    loadOrganizations();
  }, []);

  useEffect(() => {
    if (selectedApp) {
      loadRoadmap();
    }
  }, [selectedApp, selectedOrg]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/applications');
      const apps = Array.isArray(response) ? response : response?.applications || [];
      setApplications(apps);
    } catch (err) {
      console.error('Error loading applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await apiClient.get('/organizations');
      const orgs = Array.isArray(response) ? response : response?.organizations || [];
      setOrganizations(orgs);
    } catch (err) {
      console.error('Error loading organizations:', err);
    }
  };

  const loadRoadmap = async () => {
    setLoading(true);
    try {
      // Build query params
      let queryParams = `app_registry_id=${selectedApp}&type=feature_roadmap_public`;
      if (selectedOrg) {
        queryParams += `&org_id=${selectedOrg}`;
      }
      
      // Load existing roadmap from announcements API
      const response = await apiClient.get(`/announcements?${queryParams}`);
      
      if (response && response.data && response.data.length > 0) {
        // Get the most recent roadmap announcement
        const roadmap = response.data[0];
        if (roadmap.text) {
          setIntroText(roadmap.text.introduction || '');
          setFeatures(roadmap.text.bulleted_roadmap || ['']);
        }
      } else {
        // If no roadmap exists, start fresh
        console.log('No existing roadmap found, starting fresh');
        setIntroText('');
        setFeatures(['']);
      }
    } catch (err) {
      console.error('Error loading roadmap:', err);
      setIntroText('');
      setFeatures(['']);
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...features];
    newFeatures[index] = value;
    setFeatures(newFeatures);
  };

  const addFeature = () => {
    setFeatures([...features, '']);
  };

  const removeFeature = (index) => {
    if (features.length > 1) {
      const newFeatures = features.filter((_, i) => i !== index);
      setFeatures(newFeatures);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.innerHTML);
  };

  const handleDragOver = (e) => {
    if (e.preventDefault) {
      e.preventDefault();
    }
    e.dataTransfer.dropEffect = 'move';
    return false;
  };

  const handleDrop = (e, dropIndex) => {
    if (e.stopPropagation) {
      e.stopPropagation();
    }

    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      const draggedFeature = features[draggedIndex];
      const newFeatures = [...features];
      
      // Remove the dragged item
      newFeatures.splice(draggedIndex, 1);
      
      // Insert it at the new position
      newFeatures.splice(dropIndex, 0, draggedFeature);
      
      setFeatures(newFeatures);
    }

    setDraggedIndex(null);
    return false;
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const saveRoadmap = async () => {
    if (!selectedApp) {
      alert('Please select an application first');
      return;
    }

    setSaving(true);
    try {
      const roadmapData = {
        introduction: introText,
        bulleted_roadmap: features.filter(f => f.trim() !== '')
      };

      // Build query params
      let queryParams = `app_registry_id=${selectedApp}&type=feature_roadmap_public`;
      if (selectedOrg) {
        queryParams += `&org_id=${selectedOrg}`;
      }

      // Check if a roadmap already exists
      const existingResponse = await apiClient.get(`/announcements?${queryParams}`);
      
      if (existingResponse && existingResponse.data && existingResponse.data.length > 0) {
        // Update existing roadmap
        const existingId = existingResponse.data[0].id;
        await apiClient.patch(`/announcements/${existingId}`, {
          text: roadmapData
        });
        console.log('Updated existing roadmap announcement');
      } else {
        // Create new roadmap announcement
        const payload = {
          app_registry_id: parseInt(selectedApp),
          type: 'feature_roadmap_public',
          text: roadmapData
        };
        
        // Only add org_id if selected
        if (selectedOrg) {
          payload.org_id = parseInt(selectedOrg);
        }
        
        console.log('Creating new roadmap announcement:', payload);
        await apiClient.post('/announcements', payload);
      }
      
      alert('Roadmap saved successfully!');
    } catch (err) {
      console.error('Error saving roadmap:', err);
      alert('Failed to save roadmap. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const exportJSON = () => {
    const payload = {
      introduction: introText,
      bulleted_roadmap: features.filter(f => f.trim() !== '')
    };
    
    const dataStr = JSON.stringify(payload, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `roadmap-${selectedApp || 'draft'}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  return (
    <div className="feature-roadmap-builder">
      <div className="container-fluid p-0">
        <div className="row g-0">
          <div className="col-12">
            <div className="bgc-white p-20 bd m-3">
            <h4 className="c-grey-900 mB-20">
              <i className="ti-road me-2"></i>
              Feature Roadmap Builder
            </h4>
            
            {/* Application and Organization Selectors */}
            <div className="row mb-4">
              <div className="col-lg-4">
                <label className="form-label fw-500">Select Application</label>
                <select
                  className="form-select"
                  value={selectedApp}
                  onChange={(e) => setSelectedApp(e.target.value)}
                  disabled={loading}
                >
                  <option value="">-- Select an Application --</option>
                  {applications.map(app => (
                    <option key={app.id} value={app.id}>
                      {app.detail?.app_name || app.name || `App ${app.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-4">
                <label className="form-label fw-500">Select Organization (Optional)</label>
                <select
                  className="form-select"
                  value={selectedOrg}
                  onChange={(e) => setSelectedOrg(e.target.value)}
                  disabled={loading}
                >
                  <option value="">-- Select an Organization --</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.detail?.org_name || org.name || `Org ${org.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-4 d-flex align-items-end justify-content-end">
                <button 
                  className="btn btn-info me-2"
                  onClick={exportJSON}
                  disabled={!selectedApp || loading}
                >
                  <i className="ti-download me-1"></i>
                  Export JSON
                </button>
                <button 
                  className="btn btn-success"
                  onClick={saveRoadmap}
                  disabled={!selectedApp || saving || loading}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="ti-save me-1"></i>
                      Save Roadmap
                    </>
                  )}
                </button>
              </div>
            </div>

            {selectedApp && (
              <>
                {/* Introduction Section */}
                <div className="card mb-4">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                      <i className="ti-text me-2"></i>
                      Introduction Text
                    </h5>
                  </div>
                  <div className="card-body">
                    <textarea
                      className="form-control"
                      rows="4"
                      placeholder="Enter the introduction text for your feature roadmap..."
                      value={introText}
                      onChange={(e) => setIntroText(e.target.value)}
                      disabled={loading}
                    />
                    <small className="form-text text-muted">
                      This text will appear at the top of your roadmap as an introduction.
                    </small>
                  </div>
                </div>

                {/* Features Section */}
                <div className="card">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">
                      <i className="ti-list me-2"></i>
                      Feature List
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="alert alert-info mb-3">
                      <i className="ti-info-alt me-2"></i>
                      <strong>Tip:</strong> Drag and drop feature boxes to reorder them. Click the trash icon to remove a feature.
                    </div>
                    
                    {features.map((feature, index) => (
                      <div
                        key={index}
                        className="feature-box mb-3 p-3 border rounded position-relative"
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        style={{
                          cursor: 'move',
                          backgroundColor: draggedIndex === index ? '#f0f0f0' : 'var(--c-bkg-card)',
                          border: '2px solid var(--c-border)'
                        }}
                      >
                        <div className="d-flex align-items-start">
                          <div className="me-3 text-muted">
                            <i className="ti-move" style={{ fontSize: '20px', cursor: 'grab' }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <div className="input-group">
                              <span className="input-group-text">
                                #{index + 1}
                              </span>
                              <textarea
                                className="form-control"
                                rows="2"
                                placeholder={`Enter feature ${index + 1} description...`}
                                value={feature}
                                onChange={(e) => handleFeatureChange(index, e.target.value)}
                                disabled={loading}
                              />
                              {features.length > 1 && (
                                <button
                                  className="btn btn-outline-danger"
                                  type="button"
                                  onClick={() => removeFeature(index)}
                                  title="Remove this feature"
                                >
                                  <i className="ti-trash"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Add Feature Button */}
                    <div className="text-center mt-3">
                      <button
                        className="btn btn-primary btn-lg"
                        onClick={addFeature}
                        disabled={loading}
                      >
                        <i className="ti-plus me-2"></i>
                        Add New Feature
                      </button>
                    </div>
                  </div>
                </div>

                {/* JSON Preview */}
                <div className="card mt-4">
                  <div className="card-header bg-dark text-white">
                    <h6 className="mb-0">
                      <i className="ti-code me-2"></i>
                      JSON Preview
                    </h6>
                  </div>
                  <div className="card-body">
                    <pre className="bg-light p-3 rounded" style={{ maxHeight: '200px', overflow: 'auto' }}>
                      {JSON.stringify({
                        introduction: introText,
                        bulleted_roadmap: features.filter(f => f.trim() !== '')
                      }, null, 2)}
                    </pre>
                  </div>
                </div>
              </>
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureRoadmapBuilder;

// Export initialization function
export const initFeatureRoadmapBuilder = () => {
  const mountElement = document.getElementById('feature-roadmap-root');
  if (mountElement) {
    import('react').then(React => {
      import('react-dom/client').then(ReactDOM => {
        const root = ReactDOM.createRoot(mountElement);
        root.render(React.createElement(FeatureRoadmapBuilder));
      });
    });
  }
};